/**
 * BillingTable Component
 * Tabella bollette con accordion expandable per dettagli estesi (issue #75)
 */

import React, { useState } from 'react';
import api from '../utils/api';

function BillingTable({ bollette, onDelete }) {
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [dettagli, setDettagli] = useState({});
  const [loadingDettagli, setLoadingDettagli] = useState(new Set());

  const formatPeriodo = (bolletta) => {
    const periodoMesi = bolletta.periodo_mesi;
    
    // Se abbiamo date esatte dal parsing OCR, usiamo quelle
    if (bolletta.periodo_inizio && bolletta.periodo_fine) {
      const parseDate = (str) => {
        const parts = str.split(/[/-]/);
        if (parts.length !== 3) return null;
        // Supporta DD/MM/YYYY e YYYY-MM-DD
        if (parts[0].length === 4) {
          return new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
        }
        return new Date(parseInt(parts[2]), parseInt(parts[1]) - 1, parseInt(parts[0]));
      };
      
      const inizio = parseDate(bolletta.periodo_inizio);
      const fine = parseDate(bolletta.periodo_fine);
      
      if (inizio && fine) {
        if (!periodoMesi || periodoMesi === 1) {
          return inizio.toLocaleDateString('it-IT', { year: 'numeric', month: 'short' });
        }
        const meseInizio = inizio.toLocaleDateString('it-IT', { month: 'short' });
        const meseFine = fine.toLocaleDateString('it-IT', { month: 'short', year: 'numeric' });
        return `${meseInizio}-${meseFine}`;
      }
    }
    
    // Fallback: approssimazione da data_bolletta
    if (!periodoMesi || periodoMesi === 1) {
      const date = new Date(bolletta.data_bolletta);
      return date.toLocaleDateString('it-IT', { year: 'numeric', month: 'short' });
    } else {
      const dataFine = new Date(bolletta.data_bolletta);
      const dataInizio = new Date(dataFine);
      dataInizio.setMonth(dataInizio.getMonth() - periodoMesi + 1);
      
      const meseInizio = dataInizio.toLocaleDateString('it-IT', { month: 'short' });
      const meseFine = dataFine.toLocaleDateString('it-IT', { month: 'short', year: 'numeric' });
      
      return `${meseInizio}-${meseFine}`;
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('it-IT', { 
      style: 'currency', 
      currency: 'EUR' 
    }).format(amount);
  };

  const formatConsumption = (consumo, tipo) => {
    if (consumo == null) return 'N/A';
    const unit = tipo === 'luce' ? 'kWh' : 'm³';
    return `${consumo.toFixed(2)} ${unit}`;
  };

  const getTypeEmoji = (tipo) => {
    return tipo === 'luce' ? '💡' : '🔥';
  };

  // Gestisci espansione accordion
  const toggleExpand = async (bollettaId) => {
    const isExpanded = expandedRows.has(bollettaId);
    
    if (isExpanded) {
      // Collapse
      setExpandedRows(prev => {
        const next = new Set(prev);
        next.delete(bollettaId);
        return next;
      });
    } else {
      // Expand e carica dettagli se non già in cache
      if (!dettagli[bollettaId]) {
        setLoadingDettagli(prev => new Set(prev).add(bollettaId));
        
        try {
          const response = await api.get(`/bollette/${bollettaId}`);
          setDettagli(prev => ({
            ...prev,
            [bollettaId]: response.data.data.dettagli || {}
          }));
        } catch (err) {
          console.error(`Errore caricamento dettagli bolletta ${bollettaId}:`, err);
          setDettagli(prev => ({
            ...prev,
            [bollettaId]: { error: err.message }
          }));
        } finally {
          setLoadingDettagli(prev => {
            const next = new Set(prev);
            next.delete(bollettaId);
            return next;
          });
        }
      }
      
      // Espandi
      setExpandedRows(prev => new Set(prev).add(bollettaId));
    }
  };

  if (bollette.length === 0) {
    return (
      <div style={{ 
        textAlign: 'center', 
        padding: '40px', 
        color: '#999',
        border: '2px dashed #ddd',
        borderRadius: '8px'
      }}>
        <p style={{ fontSize: '48px', margin: '0 0 10px 0' }}>📭</p>
        <p>Nessuna bolletta trovata</p>
      </div>
    );
  }

  return (
    <>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ 
        width: '100%', 
        borderCollapse: 'collapse',
        fontSize: '14px'
      }}>
        <thead>
          <tr style={{ 
            background: '#f5f5f5',
            borderBottom: '2px solid #ddd'
          }}>
            <th style={{ padding: '12px 8px', textAlign: 'center', width: '40px' }}></th>
            <th style={{ padding: '12px 8px', textAlign: 'left' }}>Periodo</th>
            <th style={{ padding: '12px 8px', textAlign: 'left' }}>Tipo</th>
            <th style={{ padding: '12px 8px', textAlign: 'left' }}>Provider</th>
            <th style={{ padding: '12px 8px', textAlign: 'right' }}>Consumo</th>
            <th style={{ padding: '12px 8px', textAlign: 'right' }}>Importo</th>
            <th style={{ padding: '12px 8px', textAlign: 'center' }}>Azioni</th>
          </tr>
        </thead>
        <tbody>
          {bollette.map((bolletta, index) => (
            <React.Fragment key={bolletta.id}>
              {/* Riga principale */}
              <tr 
                style={{ 
                  borderBottom: '1px solid #eee',
                  background: index % 2 === 0 ? '#fff' : '#fafafa',
                  cursor: 'pointer'
                }}
              >
                {/* Bottone expand/collapse */}
                <td style={{ padding: '12px 8px', textAlign: 'center' }}>
                  <button
                    onClick={() => toggleExpand(bolletta.id)}
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      fontSize: '16px',
                      padding: '0',
                      lineHeight: 1,
                      color: '#1976d2',
                      transition: 'transform 0.2s'
                    }}
                    title={expandedRows.has(bolletta.id) ? 'Nascondi dettagli' : 'Mostra dettagli'}
                  >
                    {expandedRows.has(bolletta.id) ? '▼' : '▶'}
                  </button>
                </td>
                
                {/* Dati tabella */}
                <td style={{ padding: '12px 8px' }}>
                  {formatPeriodo(bolletta)}
                </td>
                <td style={{ padding: '12px 8px' }}>
                  <span style={{ 
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '4px 10px',
                    borderRadius: '12px',
                    background: bolletta.tipo === 'luce' ? '#fff3cd' : '#f8d7da',
                    fontSize: '13px',
                    fontWeight: '500'
                  }}>
                    {getTypeEmoji(bolletta.tipo)} {bolletta.tipo.charAt(0).toUpperCase() + bolletta.tipo.slice(1)}
                  </span>
                </td>
                <td style={{ padding: '12px 8px' }}>
                  {bolletta.provider}
                </td>
                <td style={{ padding: '12px 8px', textAlign: 'right', fontWeight: '500' }}>
                  {formatConsumption(bolletta.tipo === 'luce' ? bolletta.kwh : bolletta.m3, bolletta.tipo)}
                </td>
                <td style={{ 
                  padding: '12px 8px', 
                  textAlign: 'right', 
                  fontWeight: '600',
                  color: '#1976d2'
                }}>
                  {formatCurrency(bolletta.importo)}
                </td>
                <td style={{ padding: '12px 8px', textAlign: 'center' }}>
                  <button
                    onClick={() => onDelete(bolletta.id)}
                    style={{
                      padding: '6px 12px',
                      borderRadius: '4px',
                      border: '1px solid #d32f2f',
                      background: '#fff',
                      color: '#d32f2f',
                      cursor: 'pointer',
                      fontSize: '12px',
                      fontWeight: '500'
                    }}
                    onMouseOver={(e) => {
                      e.target.style.background = '#d32f2f';
                      e.target.style.color = '#fff';
                    }}
                    onMouseOut={(e) => {
                      e.target.style.background = '#fff';
                      e.target.style.color = '#d32f2f';
                    }}
                  >
                    🗑️ Elimina
                  </button>
                </td>
              </tr>
              
              {/* Riga accordion (nascosta per default) */}
              {expandedRows.has(bolletta.id) && (
                <tr style={{ 
                  background: '#f9f9f9',
                  borderBottom: '1px solid #ddd'
                }}>
                  <td colSpan="7" style={{ padding: '0' }}>
                    <AccordionDettagli 
                      bollettaId={bolletta.id}
                      tipo={bolletta.tipo}
                      dettagli={dettagli[bolletta.id]}
                      loading={loadingDettagli.has(bolletta.id)}
                    />
                  </td>
                </tr>
              )}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
    </>
  );
}

/**
 * Componente AccordionDettagli
 * Visualizza i dettagli estesi della bolletta in formato organizzato
 */
function AccordionDettagli({ bollettaId, tipo, dettagli, loading }) {
  if (loading) {
    return (
      <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
        ⏳ Caricamento dettagli...
      </div>
    );
  }

  if (!dettagli) {
    return (
      <div style={{ padding: '20px', color: '#d32f2f', background: '#ffebee', borderRadius: '4px' }}>
        ❌ Errore nel caricamento dei dettagli
      </div>
    );
  }

  if (dettagli.error) {
    return (
      <div style={{ padding: '20px', color: '#d32f2f', background: '#ffebee', borderRadius: '4px' }}>
        ❌ {dettagli.error}
      </div>
    );
  }

  // Se non ci sono dettagli, mostra messaggio
  if (Object.keys(dettagli).length === 0 || Object.values(dettagli).every(v => v === null)) {
    return (
      <div style={{ padding: '20px', color: '#999', fontStyle: 'italic' }}>
        Nessun dettaglio esteso disponibile per questa bolletta
      </div>
    );
  }

  const formatCurrency = (amount) => {
    if (amount == null) return 'N/A';
    return new Intl.NumberFormat('it-IT', { 
      style: 'currency', 
      currency: 'EUR' 
    }).format(amount);
  };

  const formatNumber = (num, decimals = 2) => {
    if (num == null) return 'N/A';
    return num.toFixed(decimals);
  };

  return (
    <div style={{ padding: '16px', background: '#fff' }}>
      {/* Breakdown Costi */}
      {(dettagli.costo_energia || dettagli.costo_trasporto || dettagli.costo_oneri) && (
        <section style={{ marginBottom: '20px' }}>
          <h4 style={{ fontSize: '14px', margin: '0 0 12px 0', color: '#1976d2' }}>
            💰 Breakdown Costi
          </h4>
          <div style={{
            background: '#f5f5f5',
            borderRadius: '6px',
            padding: '12px',
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '12px'
          }}>
            {dettagli.costo_energia != null && (
              <DetailItem label="Energia" value={formatCurrency(dettagli.costo_energia)} />
            )}
            {dettagli.costo_trasporto != null && (
              <DetailItem label="Trasporto" value={formatCurrency(dettagli.costo_trasporto)} />
            )}
            {dettagli.costo_oneri != null && (
              <DetailItem label="Oneri Sistema" value={formatCurrency(dettagli.costo_oneri)} />
            )}
            {dettagli.costo_accise != null && (
              <DetailItem label="Accise" value={formatCurrency(dettagli.costo_accise)} />
            )}
            {dettagli.costo_iva != null && (
              <DetailItem label="IVA" value={formatCurrency(dettagli.costo_iva)} />
            )}
            {dettagli.costo_dispacciamento != null && (
              <DetailItem label="Dispacciamento" value={formatCurrency(dettagli.costo_dispacciamento)} />
            )}
          </div>
        </section>
      )}

      {/* Caratteristiche Contratto */}
      {(dettagli.prezzo_energia_kwh || dettagli.quota_fissa_mensile || dettagli.potenza_impegnata || dettagli.tipo_tariffa || dettagli.tipo_offerta) && (
        <section style={{ marginBottom: '20px' }}>
          <h4 style={{ fontSize: '14px', margin: '0 0 12px 0', color: '#1976d2' }}>
            ⚙️ Caratteristiche Contratto
          </h4>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
            gap: '12px'
          }}>
            {dettagli.prezzo_energia_kwh != null && (
              <DetailItem 
                label={tipo === 'luce' ? 'Prezzo Energia' : 'Prezzo Gas'} 
                value={`${formatNumber(dettagli.prezzo_energia_kwh, 4)} €/${tipo === 'luce' ? 'kWh' : 'Smc'}`} 
              />
            )}
            {dettagli.quota_fissa_mensile != null && (
              <DetailItem label="Quota Fissa" value={`${formatCurrency(dettagli.quota_fissa_mensile)}/mese`} />
            )}
            {dettagli.potenza_impegnata != null && (
              <DetailItem label="Potenza Impegnata" value={`${formatNumber(dettagli.potenza_impegnata)} kW`} />
            )}
            {dettagli.tipo_tariffa && (
              <DetailItem label="Tipo Tariffa" value={dettagli.tipo_tariffa.charAt(0).toUpperCase() + dettagli.tipo_tariffa.slice(1)} />
            )}
            {dettagli.tipo_offerta && (
              <DetailItem label="Tipo Offerta" value={dettagli.tipo_offerta} />
            )}
            {dettagli.pun != null && (
              <DetailItem label="PUN" value={`${formatNumber(dettagli.pun)} €/MWh`} />
            )}
          </div>
        </section>
      )}

      {/* Fasce Orarie */}
      {(dettagli.consumo_f1 || dettagli.consumo_f2 || dettagli.consumo_f3) && (
        <section style={{ marginBottom: '20px' }}>
          <h4 style={{ fontSize: '14px', margin: '0 0 12px 0', color: '#1976d2' }}>
            ⏰ Fasce Orarie
          </h4>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
            gap: '12px'
          }}>
            {dettagli.consumo_f1 != null && (
              <FasciaCard label="F1 (Picco)" consumo={dettagli.consumo_f1} prezzo={dettagli.prezzo_f1} />
            )}
            {dettagli.consumo_f2 != null && (
              <FasciaCard label="F2 (Intermedia)" consumo={dettagli.consumo_f2} prezzo={dettagli.prezzo_f2} />
            )}
            {dettagli.consumo_f3 != null && (
              <FasciaCard label="F3 (Fuori Picco)" consumo={dettagli.consumo_f3} prezzo={dettagli.prezzo_f3} />
            )}
          </div>
        </section>
      )}

      {/* Info Contrattuali */}
      {(dettagli.codice_pod || dettagli.codice_pdr || dettagli.numero_cliente || dettagli.metodo_pagamento) && (
        <section style={{ marginBottom: '0' }}>
          <h4 style={{ fontSize: '14px', margin: '0 0 12px 0', color: '#1976d2' }}>
            📝 Info Contrattuali
          </h4>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
            gap: '12px'
          }}>
            {dettagli.codice_pod && (
              <DetailItem label="POD" value={dettagli.codice_pod} mono />
            )}
            {dettagli.codice_pdr && (
              <DetailItem label="PDR" value={dettagli.codice_pdr} mono />
            )}
            {dettagli.numero_cliente && (
              <DetailItem label="Numero Cliente" value={dettagli.numero_cliente} />
            )}
            {dettagli.numero_contratto && (
              <DetailItem label="Numero Contratto" value={dettagli.numero_contratto} />
            )}
            {dettagli.metodo_pagamento && (
              <DetailItem label="Metodo Pagamento" value={dettagli.metodo_pagamento.toUpperCase()} />
            )}
            {dettagli.frequenza_fattura && (
              <DetailItem label="Frequenza Fattura" value={dettagli.frequenza_fattura.charAt(0).toUpperCase() + dettagli.frequenza_fattura.slice(1)} />
            )}
            {dettagli.scadenza_contratto && (
              <DetailItem label="Scadenza Contratto" value={dettagli.scadenza_contratto} />
            )}
            {dettagli.bonus_sociale != null && (
              <DetailItem label="Bonus Sociale" value={formatCurrency(dettagli.bonus_sociale)} />
            )}
          </div>
        </section>
      )}
    </div>
  );
}

/**
 * Componente DetailItem
 * Cella informativa con label e valore
 */
function DetailItem({ label, value, mono }) {
  return (
    <div style={{
      background: '#f9f9f9',
      borderRadius: '4px',
      padding: '8px',
      borderLeft: '3px solid #1976d2',
    }}>
      <div style={{ fontSize: '11px', color: '#666', marginBottom: '4px', fontWeight: '600' }}>
        {label}
      </div>
      <div style={{ 
        fontSize: '13px', 
        fontWeight: '500',
        color: '#333',
        fontFamily: mono ? 'monospace' : 'inherit',
        wordBreak: 'break-all'
      }}>
        {value || 'N/A'}
      </div>
    </div>
  );
}

/**
 * Componente FasciaCard
 * Visualizza informazioni di una fascia oraria
 */
function FasciaCard({ label, consumo, prezzo }) {
  return (
    <div style={{
      background: '#e8f5e9',
      borderRadius: '4px',
      padding: '8px',
      borderLeft: '3px solid #4caf50',
    }}>
      <div style={{ fontSize: '11px', color: '#2e7d32', marginBottom: '4px', fontWeight: '600' }}>
        {label}
      </div>
      <div style={{ fontSize: '13px', fontWeight: '600', color: '#1b5e20' }}>
        {formatNumber(consumo, 2)} kWh
      </div>
      {prezzo != null && (
        <div style={{ fontSize: '12px', color: '#388e3c', marginTop: '4px' }}>
          {formatNumber(prezzo, 4)} €/kWh
        </div>
      )}
    </div>
  );
}

function formatNumber(num, decimals = 2) {
  if (num == null) return 'N/A';
  return num.toFixed(decimals);
}

export default BillingTable;
